import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { ActionsTable, HeaderTables, Order, SortType } from "../../models";
import { SpinnerComponent } from "../spinner/spinner.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CommonModule, NgForOf, NgIf } from "@angular/common";
import { TableActionButtonsComponent } from "../table-action-buttons/table-action-buttons.component";
import { AppTableGenericButtonComponent } from "../actions/app-table-generic-button/app-table-generic-button.component";
import { v4 as uuidv4 } from "uuid";
import { DomSanitizer } from "@angular/platform-browser";
import { FormsModule } from "@angular/forms";

@Component({
  selector: "app-table",
  standalone: true,
  imports: [NgbTooltipModule, SpinnerComponent, TranslateModule, NgIf, NgForOf, TableActionButtonsComponent, AppTableGenericButtonComponent, CommonModule, FormsModule],
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
})
export class TableComponent implements OnInit, OnChanges {
  @Input() headers: HeaderTables[] = [];
  @Input() columnWidth: Array<any> = [];
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() itemsPerPage: number = 10;
  @Input() totalItems: number = 1;
  @Input() data: Array<any> = [];
  @Input() loading = false;
  @Input() actions: ActionsTable[] = [];
  @Input() customNoData: string = "no_data";
  @Output() handlePageChange = new EventEmitter<any>();
  @Output() handleItemsPerPageChange = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<SortType>();
  @Output() onFilterChange = new EventEmitter<{ key: string, value: string }>();

  pagedData: any[] = [];
  sortColumn: string = "";
  sortOrder: Order = Order.ASC;
  pageSizes: number[] = [10, 25, 50];
  searchFilters: { [key: string]: string } = {};
  filteredData: any[] = [];

  constructor(private sanitizer: DomSanitizer, private translateService: TranslateService) {}

  ngOnInit(): void {
    this.filteredData = [...this.data];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] && changes["data"].currentValue) {
      this.filteredData = [...this.data];
      this.applyFilters();
    }
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.handlePageChange.emit(page);
    }
  }

  trackByAction(index: number, action: any) {
    const i = uuidv4();
    return `${index}${action}${i}`;
  }

  trackByItem(index: number, item: any): any {
    // Use item.id if it exists and is not empty, otherwise fall back to index
    return item?.id !== null && item?.id !== undefined && item?.id !== "" ? item.id : index;
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortOrder = this.sortOrder === Order.ASC ? Order.DESC : Order.ASC;
    } else {
      this.sortColumn = column;
      this.sortOrder = Order.ASC;
    }
    const sortEvent: SortType = {
      _sort: this.sortColumn,
      _order: this.sortOrder,
    };
    this.sortChanged.emit(sortEvent);
  }

  renderSafeHtml(value: string, html: boolean = false) {
    if (html) {
      return this.sanitizer.bypassSecurityTrustHtml(value);
    }
    return value;
  }

  getItem(item: any, headerKey: string, headerTranslateValue: boolean) {
    item.headerKey;
    if (headerTranslateValue === true && item[headerKey]) {
      return this.translateService.instant(item[headerKey]);
    }
    return item[headerKey];
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];

    // Se il numero di pagine è piccolo, mostriamo tutte le pagine senza "..."
    if (this.totalPages <= 7) {
      return Array.from({ length: this.totalPages - 2 }, (_, i) => i + 2);
    }

    if (this.currentPage <= 4) {
      pages.push(2, 3, 4, 5);
    } else if (this.currentPage >= this.totalPages - 4) {
      pages.push(this.totalPages - 4, this.totalPages - 3, this.totalPages - 2, this.totalPages - 1);
    } else {
      pages.push(this.currentPage - 2, this.currentPage - 1, this.currentPage, this.currentPage + 1, this.currentPage + 2);
    }

    return pages;
  }

  onChangeItemsPerPage(event: any): void {
    this.handleItemsPerPageChange.emit(event?.target?.value ?? 10);
  }

  onSearchChange(columnKey: string, searchValue: string): void {
    this.searchFilters[columnKey] = searchValue;
    //this.applyFilters();  BE Search
    this.onFilterChange.emit({
      key: columnKey,
      value: searchValue
    });
  }

  applyFilters(): void {
    this.filteredData = this.data.filter((item) => {
      return Object.keys(this.searchFilters).every((columnKey) => {
        const searchValue = this.searchFilters[columnKey].toLowerCase().trim();
        if (!searchValue) return true;

        const itemValue = this.getItem(item, columnKey, false);
        if (itemValue === null || itemValue === undefined) return false;

        return String(itemValue).toLowerCase().includes(searchValue);
      });
    });
  }

  clearSearch(columnKey: string): void {
    this.searchFilters[columnKey] = "";
    this.applyFilters();
  }

  getDisplayData(): any[] {
    return this.filteredData;
  }

  hasSearchableHeaders(): boolean {
    return this.headers.some((header) => header.searchable);
  }

  getSearchPlaceholder(headerLabel: string): string {
    return `${this.translateService.instant("forms.search")} ${this.translateService.instant(headerLabel)}`;
  }

  onSearchInput(event: Event, columnKey: string): void {
    const target = event.target as HTMLInputElement;
    this.onSearchChange(columnKey, target.value);
  }
}
